<template>
  <div>
    <!-- Hero section -->
    <section class="relative overflow-hidden hero-sayhi-gradient-bg">
      <div class="mx-auto mt-20 flex max-w-6xl flex-col gap-10 px-6 py-12 md:flex-row md:items-center md:py-16">
        <!-- Left column -->
        <div class="md:w-1/2 space-y-6">
          <div class="space-y-3">
            <img :src="imgSrc($t('caseStudies.CS_strategic-design.logoProduct'))" alt="Amazon logo" class="w-20"/>
            <h1 class="text-3xl font-semibold sm:text-4xl lg:text-5xl text-slate-700 lg:leading-[1.2]">
              {{ $t('caseStudies.CS_sayhi.title') }}
            </h1>
            <p class="max-w-xl text-md text-slate-700 sm:text-base lg:text-lg">
              {{ $t('caseStudies.CS_sayhi.title_description') }}
            </p>
          </div>

          <!-- Tags -->
          <div class="flex flex-wrap gap-2 md:gap-4 pt-2">
            <span class="inline-flex items-center rounded-full border border-slate-400 px-3 py-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              Product Design
            </span>
            <span class="inline-flex items-center rounded-full border border-slate-400 px-3 py-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              UX research
            </span>
            <span class="inline-flex items-center rounded-full border border-slate-400 px-3 py-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              Web application
            </span>
          </div>
        </div>

        <!-- Right column -->
        <div class="md:w-1/2 flex justify-center hidden md:block">
          <img :src="imgSrc($t('caseStudies.CS_sayhi.heroImage'))" 
          alt="smartphone with sayhi screen" 
          class="max-w-[450px]"/>
        </div>
      </div>
    </section>
    <!-- End of Hero section -->

    <!-- intro-section -->
    <section class="flex flex-col text-normal leading-normal mx-auto max-w-6xl px-6 py-8 dark:text-white">
      <ul class="list-none flex flex flex-wrap gap-y-4 gap-x-8 pt-2 items-end justify-start text-xl dark:text-white">
        <!-- Role detail -->
        <li class="flex flex-col text-slashdark dark:text-white">
          <span class="text-lg">My role</span> 
          <span class="font-bold">Sr. UX designer</span> 
        </li>
        <!-- Timeline/team details -->
        <li v-for="(li,idx) in $tm('caseStudies.CS_sayhi.projectDetails')" :key="idx" class="font-semibold text-gray-600 dark:text-white">
          <span v-if="li.iconURL" class="flex flex-row gap-2 items-center">  
            <img v-if="li.iconURL" :src="iconSrc(li.iconURL)" :alt="li.description" class="w-5" />
            {{li.description}}
          </span>
          <span v-else>
            {{li.description}}
          </span>
        </li>
      </ul>
      <p class="text-slate-700 font-light text-xl leading-normal py-4 lg:text-2xl lg:leading-normal dark:text-white">{{ $t('caseStudies.CS_sayhi.intro-section') }}</p>
    </section>
    <!-- End/intro-section -->

    <!-- Presentation metrics  -->
    <section class="flex flex-col mx-auto max-w-7xl p-8 mb-16 lg:items-center xl:px-6 2xl:px-0">

       <!-- block-00 -->
       <div class="grid grid-cols-2 gap-8 h-auto">
          <div class="col-span-2 lg:col-span-1 order-2 lg:order-1">
            <img 
              :src="imgSrc($t('caseStudies.CS_sayhi.proposal-section.block00.image.url'))" 
              :alt="$t('caseStudies.CS_sayhi.proposal-section.block00.image.alt')" 
              class="w-full md:w-[650px] lg:w-full h-auto"/>
          </div>
          <div class="col-span-2 lg:col-span-1 flex flex-col justify-center gap-2 order-1 lg:order-2">
              <img :src="imgSrc($t('caseStudies.CS_sayhi.proposal-section.block00.logo'))" class="flex w-24 max-w-24">
              <h3 class="text-[24px] md:text-[28px] text-slashblue font-medium dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block00.title') }}</h3>
              <p class="text-slashdark font-light text-lg leading-normal dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block00.description') }}</p> 
          </div>
       </div>
       <!-- END / block-00 -->

       <!-- block-01 -->
       <div class="grid grid-cols-2 gap-8 mt-10 md:mt-20 h-auto">
          <div class="col-span-2 lg:col-span-1 flex flex-col justify-start gap-2 max-w-sm">
              <h3 class="text-[24px] text-left md:text-right mt-2 text-slashblue font-medium dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block01.title') }}</h3>
              <p class="text-slashdark text-left md:text-right font-light text-lg leading-normal dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block01.description') }}</p> 
          </div>
          <div class="col-span-2 lg:col-span-1">
            <img 
              :src="imgSrc($t('caseStudies.CS_sayhi.proposal-section.block01.image.url'))" 
              :alt="$t('caseStudies.CS_sayhi.proposal-section.block01.image.alt')" 
              class="w-[375px] h-auto"/>
          </div>
       </div>
       <!-- END / block-01 -->

       <!-- block-02 -->
       <div class="grid grid-cols-2 gap-8 mt-10 md:mt-20 h-auto">
          <div class="col-span-2 lg:col-span-1 order-2 lg:order-1 max-w-sm">
            <img 
              :src="imgSrc($t('caseStudies.CS_sayhi.proposal-section.block02.image.url'))" 
              :alt="$t('caseStudies.CS_sayhi.proposal-section.block02.image.alt')" 
              class="w-[320px] h-auto"/>
          </div>
          <div class="col-span-2 lg:col-span-1 flex flex-col justify-start gap-2 order-1 lg:order-2">
              <h3 class="text-[24px] mt-2 text-slashblue font-medium dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block02.title') }}</h3>
              <p class="text-slashdark font-light text-lg leading-normal dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block02.description') }}</p>
              <ul class="grid grid-cols-2 gap-6 list-none mt-4 text-slashdark font-light text-lg leading-normal dark:text-white">
                <li v-for="(core,idx) in $tm('caseStudies.CS_sayhi.proposal-section.block02.coreFeatures')" :key="idx" class="flex flex-col gap-2">
                  <img 
                    :src="iconSrc(core.icon)" 
                    alt="" 
                    class="w-8"/>
                    <p class="text-lg">{{ core.title }}</p>
                </li>
              </ul>
          </div>
       </div>
       <!-- END / block-02 -->

       <!-- block-03 -->
       <div class="grid grid-cols-2 lg:grid-cols-3 gap-8 mt-10 md:mt-32 h-auto w-full max-w-4xl">
          <div class="col-span-2 lg:col-span-1 flex flex-col justify-start items-endt gap-2 max-w-sm">
              <h3 class="text-[24px] text-left md:text-right mt-2 text-slashblue font-medium dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block03.title') }}</h3>
              <p class="text-slashdark text-left md:text-right font-light text-lg leading-normal dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block03.description') }}</p> 
          </div>
          <div class="col-span-2 lg:col-span-2">
            <img 
              :src="imgSrc($t('caseStudies.CS_sayhi.proposal-section.block03.image.url'))" 
              :alt="$t('caseStudies.CS_sayhi.proposal-section.block03.image.alt')" 
              class="w-full h-auto"/>
          </div>
       </div>
       <!-- END / block-03 -->






       <!-- END /CASE -->
    </section>
    <!-- END / Presentation Metrics -->

    <!-- Problem-statement -->
    <section class="relative overflow-hidden text-slate-700 bg-[radial-gradient(circle_at_center,theme(colors.gray.100),theme(colors.indigo.100))]">
      <div class="mx-auto flex max-w-6xl flex-col gap-10 px-6 py-12 md:flex-row md:items-center md:py-16 lg:py-20">
        <!-- Left column -->
        <div class="md:w-1/2 space-y-6">
          <div class="space-y-3">
            <h2 class="text-3xl h-auto dark:text-white font-semibold text-slate-700">
              {{ $t('caseStudies.CS_sayhi.problem-statement-section.title') }}
            </h2>
            <p class="max-w-xl text-md text-slate-700 sm:text-base lg:text-lg">
              {{ $t('caseStudies.CS_sayhi.problem-statement-section.content') }}
            </p>
          </div>

          <!-- Tags -->
          <div class="flex flex-wrap gap-2 md:gap-4 pt-2">
            <span v-for="(tag, idx) in $tm('caseStudies.CS_sayhi.problem-statement-section.tags')" :key="idx" 
            class="inline-flex items-center rounded-full border border-slate-400 px-3 py-1 text-xs font-medium uppercase tracking-wide text-slate-700 bg-white/50 dark:text-white">
              {{tag}}
            </span>
          </div>
        </div>

        <!-- Right column -->
        <div class="md:w-1/2 flex justify-center hidden md:block">
          <img 
          :src="iconSrc($t('caseStudies.CS_sayhi.problem-statement-section.image.URL'))" 
          :alt="$t('caseStudies.CS_sayhi.problem-statement-section.image.alt')" 
          class="max-w-[450px]"/>
        </div>
      </div>
    </section>
    <!-- END/ Problem-statement -->


    <!-- Legacy experience -->
    <section class="flex flex-col gap-8 text-slate-700 text-base leading-normal mx-auto max-w-6xl px-6 py-8 space-y-4 mt-16">
      <div class="flex flex-col space-y-4">
        <h2 class="text-2xl h-auto max-w-prose dark:text-white">{{ $t('caseStudies.CS_sayhi.legacy-section.title') }}</h2>
        <p class="text-base text-slate-700 leading-relaxed dark:text-slate-300">{{ $t('caseStudies.CS_sayhi.legacy-section.content') }}</p>
      </div>
      <div class="w-full flex flex-row items-center justify-center">
       <img 
        :src="iconSrc($t('caseStudies.CS_sayhi.legacy-section.image.url'))" 
        :alt="$t('caseStudies.CS_sayhi.legacy-section.image.alt')" 
        class="w-full md:max-w-[60%]"/>
      </div>
    </section>
    <!-- END / Legacy experiece -->

    <!-- Approach -->
    <section class="flex flex-col gap-8 text-slate-700 text-base leading-normal mx-auto max-w-6xl px-6 py-8">
      <div class="flex flex-col space-y-4">
        <h2 class="text-2xl h-auto max-w-prose dark:text-white">{{ $t('caseStudies.CS_sayhi.approach-section.title') }}</h2>
        <p v-for="(para,idx) in $tm('caseStudies.CS_sayhi.approach-section.content')" :key="idx" class="text-base text-slate-700 leading-relaxed dark:text-slate-300">{{ para }}</p>
      </div>
      <div class="w-full flex flex-col items-center justify-center">
       <img 
        :src="iconSrc($t('caseStudies.CS_sayhi.approach-section.image.url'))" 
        :alt="$t('caseStudies.CS_sayhi.approach-section.image.alt')" 
        class="hidden w-full md:max-w-[80%] mx-auto md:block"/>

        <!-- mobile -->
       <img 
        :src="iconSrc($t('caseStudies.CS_sayhi.approach-section.image.urlMobile'))" 
        :alt="$t('caseStudies.CS_sayhi.approach-section.image.alt')" 
        class="w-full md:max-w-[80%] mx-auto md:hidden"/>
      </div>
    </section>
    <!-- END / Approach -->

    <!-- UX research -->
    <section class="flex flex-col gap-2 md:gap-8 text-slate-700 text-base leading-normal mx-auto max-w-6xl px-6 py-8">
      <div class="flex flex-col space-y-4">
        <h2 class="text-2xl h-auto max-w-prose dark:text-white">{{ $t('caseStudies.CS_sayhi.research-section.title') }}</h2>
        <p v-for="(para,idx) in $tm('caseStudies.CS_sayhi.research-section.content')" :key="idx" class="text-base text-slate-700 leading-relaxed dark:text-slate-300">{{ para }}</p>
      </div>
      
        <imageLauncherVue
          :src="CS_sayhi_research_images[0].src"
          :alt="CS_sayhi_research_images[0].alt"
          :startIndex="0"
          :images="CS_sayhi_research_images"
          class="w-full md:max-w-[80%] mx-auto"
        />

    </section>
    <!-- END / UX research -->

    <!-- Handover -->
    <section class="flex flex-col gap-2 md:gap-8 flex flex-col gap-2 md:gap-8 text-slate-700 text-base leading-normal mx-auto max-w-6xl px-6 py-8">
      <div class="flex flex-col space-y-4">
        <h2 class="text-2xl h-auto max-w-prose dark:text-white">{{ $t('caseStudies.CS_sayhi.handover-section.title') }}</h2>
        <p v-for="(para,idx) in $tm('caseStudies.CS_sayhi.handover-section.content')" :key="idx" class="text-base text-slate-700 leading-relaxed dark:text-slate-300">{{ para }}</p>
      </div>
        <imageLauncherVue
          :src="CS_sayhi_handover_images[0].src"
          :alt="CS_sayhi_handover_images[0].alt"
          :startIndex="0"
          :images="CS_sayhi_handover_images"
          class="w-full md:max-w-[80%] mx-auto"
        />
    </section>
    <!-- END / Handover -->

    <!-- The solution -->
    <section class="flex flex-col mx-auto max-w-7xl p-8 lg:p-12 lg:items-center xl:px-6 2xl:px-0">

      <!-- Intro proposal -->
        <div class="flex flex-col gap-4 items-center">
          <img :src="iconSrc($t('caseStudies.CS_sayhi.proposal-section.intro.iconURL'))" alt="results icon" class="flex w-14 md:w-16 lg:w-26 xl:w-[85px]"/>
          <h2 class="text-3xl h-auto max-w-prose mb-6 dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.intro.title') }}</h2>
        </div>
        <p class="text-slashdark font-light text-xl text-center leading-normal lg:text-2xl lg:leading-normal dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.intro.content') }}</p>
        <!-- END / Intro proposal -->


       <!-- CASE -->

       <!-- block-04 -->
       <div class="grid grid-cols-2 lg:grid-cols-1 gap-8 mt-12 lg:mt-24 h-auto w-full">
          <!-- <div class="col-span-2 flex flex-col justify-start items-center gap-2">
              <h3 class="text-[24px] mt-2 text-center text-slashblue font-medium dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block04.title') }}</h3>
              <p class="text-slashdark font-light text-center text-lg leading-normal dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.block04.description') }}</p> 
          </div> -->
          <div class="col-span-2 lg:col-span-1 grid grid-cols-2 gap-4">
            <imageLauncherVue
              :src="CS_sayhi_desktop_images[0].src"
              :alt="CS_sayhi_desktop_images[0].alt"
              :startIndex="0"
              :images="CS_sayhi_desktop_images"
              :borderClass="'border border-slate-300'"
              :shadowClass="'shadow-sm transition-shadow duration-200 hover:shadow-lg'"
              class="w-full"
            />
            <imageLauncherVue
              :src="CS_sayhi_desktop_images[1].src"
              :alt="CS_sayhi_desktop_images[1].alt"
              :startIndex="1"
              :images="CS_sayhi_desktop_images"
              :borderClass="'border border-slate-300'"
              :shadowClass="'shadow-sm transition-shadow duration-200 hover:shadow-lg'"
              class="w-full"
            />
            <imageLauncherVue
              :src="CS_sayhi_desktop_images[2].src"
              :alt="CS_sayhi_desktop_images[2].alt"
              :startIndex="2"
              :images="CS_sayhi_desktop_images"
              :borderClass="'border border-slate-300'"
              :shadowClass="'shadow-sm transition-shadow duration-200 hover:shadow-lg'"
              class="w-full"
            />
            <imageLauncherVue
              :src="CS_sayhi_desktop_images[3].src"
              :alt="CS_sayhi_desktop_images[3].alt"
              :startIndex="3"
              :images="CS_sayhi_desktop_images"
              :borderClass="'border border-slate-300'"
              :shadowClass="'shadow-sm transition-shadow duration-200 hover:shadow-lg'"
              class="w-full"
            />
            <imageLauncherVue
              :src="CS_sayhi_desktop_images[4].src"
              :alt="CS_sayhi_desktop_images[4].alt"
              :startIndex="4"
              :images="CS_sayhi_desktop_images"
              :borderClass="'border border-slate-300'"
              :shadowClass="'shadow-sm transition-shadow duration-200 hover:shadow-lg'"
              class="w-full"
            />
            <imageLauncherVue
              :src="CS_sayhi_desktop_images[5].src"
              :alt="CS_sayhi_desktop_images[5].alt"
              :startIndex="5"
              :images="CS_sayhi_desktop_images"
              :borderClass="'border border-slate-300'"
              :shadowClass="'shadow-sm transition-shadow duration-200 hover:shadow-lg'"
              class="w-full"
            />
          </div>
       </div>
       <!-- END / block-04 -->


      <!-- The results -->
      <div class="flex flex-col gap-4 items-center mt-12 lg:mt-20">
        <img :src="iconSrc($t('caseStudies.CS_sayhi.proposal-section.results.iconURL'))" alt="results icon" class="flex w-14 md:w-16 lg:w-26 xl:w-[85px]"/>
        <h2 class="text-3xl h-auto max-w-prose mb-6 dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.results.title') }}</h2>
      </div>
      <p class="text-slashdark font-light text-xl text-center leading-normal lg:text-2xl lg:leading-normal dark:text-white">{{ $t('caseStudies.CS_sayhi.proposal-section.results.content') }}</p>

        <!-- table metrics -->
        <div class="w-full flex mx-auto p-16 hidden md:block">
            <table class="w-full border-collapse">
              <!-- Header Row -->
              <thead>
                <tr class="border-b border-gray-200">
                  <th class="text-left text-sm font-semibold text-gray-400 pb-3 pr-8 w-1/4">Metric</th>
                  <th class="text-left text-sm font-semibold text-gray-400 pb-3 pr-8 w-1/4">before</th>
                  <th class="text-left text-sm font-semibold text-gray-400 pb-3 pr-8 w-1/4">after</th>
                  <th class="text-left text-sm font-semibold text-gray-400 pb-3 w-1/4">Impact</th>
                </tr>
              </thead>
        
              <!-- Body -->
              <tbody>
                <!-- Row 1 -->
                <tr class="border-b border-gray-100">
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base font-semibold text-gray-900">User base</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-400">1M employees</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-700">1.5M employees</span>
                  </td>
                  <td class="py-5 align-top">
                    <span class="text-base font-medium text-cyan-600">Worldwide operations <br>Frontline + Corporate</span>
                  </td>
                </tr>
        
                <!-- Row 2 -->
                <tr class="border-b border-gray-100">
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base font-semibold text-gray-900">Annual translations</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-400">52k translations/year</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-700">3.1M translations/year</span>
                  </td>
                  <td class="py-5 align-top">
                    <span class="text-base font-medium text-cyan-600">60x increase</span>
                  </td>
                </tr>
        
                <!-- Row 3 -->
                <tr class="border-b border-gray-200">
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base font-semibold text-gray-900">Languages</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-400">62 languages</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-700">97 languages and 127 dialects</span>
                  </td>
                  <td class="py-5 align-top">
                    <span class="text-base font-medium text-cyan-600">Smoother cross-team collaboration</span>
                  </td>
                </tr>

                <!-- Row 4 -->
                <tr class="border-b border-gray-200">
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base font-semibold text-gray-900">Multi devices</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-400">1 Device</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-700">5 devices</span>
                  </td>
                  <td class="py-5 align-top">
                    <span class="text-base font-medium text-cyan-600">Mobile, Desktop, Hand scanners, Kindle, Tablets</span>
                  </td>
                </tr>

                <!-- Row 5 -->
                <tr class="border-b border-gray-200">
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base font-semibold text-gray-900">Accessibility</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-400">Non-compliant</span>
                  </td>
                  <td class="py-5 pr-8 align-top">
                    <span class="text-base text-gray-700">WCAG-aligned across 97 languages</span>
                  </td>
                  <td class="py-5 align-top">
                    <span class="text-base font-medium text-cyan-600">Inclusive by design </span>
                  </td>
                </tr>
              </tbody>
            </table>
        </div>
        <!-- END/table metrics -->


        <!-- ─── MOBILE CARDS (hidden on desktop) ─── -->
        <div class="flex flex-col gap-3 md:hidden mt-12">
      
          <!-- Card 1 -->
          <div class="bg-white rounded-xl border border-gray-300 shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b bg-gray-100 border-gray-100 flex items-start justify-start gap-2">
              <span class="text-sm font-semibold text-gray-900">User base</span>
            </div>
            <div class="grid grid-cols-2 divide-x divide-gray-100">
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">After</p>
                <p class="text-sm font-medium text-cyan-600">1.5M employees</p>
              </div> 
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">Before</p>
                <p class="text-sm text-gray-500">1M employees</p>
              </div>

            </div>
          </div>
      
          <!-- Card 2 -->
          <div class="bg-white rounded-xl border border-gray-300 shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b bg-gray-100 border-gray-100 flex items-start justify-start gap-2">
              <span class="text-sm font-semibold text-gray-900">Annual translations</span>
            </div>
            <div class="grid grid-cols-2 divide-x divide-gray-100">
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">After</p>
                <p class="text-sm font-medium text-cyan-600">3.1M / year</p>
              </div>
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">Before</p>
                <p class="text-sm text-gray-500">52k / year</p>
              </div>

            </div>
          </div>
      
          <!-- Card 3 -->
          <div class="bg-white rounded-xl border border-gray-300 shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b bg-gray-100 border-gray-100 flex items-start justify-start gap-2">
              <span class="text-sm font-semibold text-gray-900">Languages</span>
            </div>
            <div class="grid grid-cols-2 divide-x divide-gray-100">
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">After</p>
                <p class="text-sm font-medium text-cyan-600">97 languages & 127 dialects</p>
              </div>
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">Before</p>
                <p class="text-sm text-gray-500">62 languages</p>
              </div>
            </div>
          </div>
      
          <!-- Card 4 -->
          <div class="bg-white rounded-xl border border-gray-300 shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b bg-gray-100 border-gray-100 flex items-start justify-start gap-2">
              <span class="text-sm font-semibold text-gray-900">Multi devices</span>
              <span class="text-xs text-gray-700">|</span>
              <span class="text-xs text-gray-500 py-0.5 rounded-full">5 devices</span>
            </div>
            <div class="grid grid-cols-2 divide-x divide-gray-100">
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">After</p>
                <p class="text-sm font-medium text-cyan-600">5 devices</p>
              </div>
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">Before</p>
                <p class="text-sm text-gray-500">1 device</p>
              </div>
            </div>
          </div>
      
          <!-- Card 5 -->
          <div class="bg-white rounded-xl border border-gray-300 shadow-sm overflow-hidden">
            <div class="px-4 py-3 border-b bg-gray-100 border-gray-100 flex items-start justify-start gap-2">
              <span class="text-sm font-semibold text-gray-900">Accessibility</span>
              <span class="text-xs text-gray-700">|</span>
              <span class="text-xs text-gray-500 py-0.5 rounded-full">WCAG-aligned</span>
            </div>
            <div class="grid grid-cols-2 divide-x divide-gray-100">
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">After</p>
                <p class="text-sm font-medium text-cyan-600">WCAG-aligned across 97 languages</p>
              </div>
              <div class="px-4 py-3">
                <p class="text-xs text-gray-400 mb-1">Before</p>
                <p class="text-sm text-gray-500">Non-compliant</p>
              </div>
            </div>
          </div>
      
        </div>



        <!-- Buttons -->
        <div class="flex flex-col w-full items-center justify-center gap-4 mt-16 md:mt-0">
          <p class="text-gray-400 font-light text-xl text-left leading-normal dark:text-white">Explore other cases</p>
          <ul class="flex flex-wrap list-none items-center justify-center gap-4">
              <li v-for="(button, idx) in $tm('caseStudies.CS_sayhi.proposal-section.results.buttons')" :key="idx" class="flex flex-wrap items-center">
                <span class="h-auto">
                  <router-link
                    v-if="!isExternal(button.url)"
                    :to="`/${$i18n.locale}`+ button.url"
                    class="no-underline"
                  >
                    <button :class="`${button.style} btn-green-secondary rounded-xl text-sm`">{{button.text}}</button>
                  </router-link>
                  <a v-else :href="button.url" :aria-label="button.text" target="_blank" :class="`${button.style} rounded-xl text-sm`">{{button.text}}</a>
                </span>
              </li>
          </ul>
        </div>
        <!-- END/ buttons -->



       <!-- END /CASE -->
    </section>
    <!-- END / The proposal -->

  </div>
</template>

<script>
import {useI18n} from 'vue-i18n';
import imageLauncherVue from '../components/imageLauncher.vue';

export default {
  name: "Contact-me",
  components: {
    imageLauncherVue
  },
  methods: {
    isExternal(url) {
      // crude check: external if starts with http.
      // Used to determine what type of button to use (router-link vs link) .
      return /^https?:\/\//.test(url);
    }
  },
  computed: {
    // Computed properties are cached getters, not functions.
    // so we return a function to use parameters such fileName.
    imgSrc() {
      return (urlFile) => {
        try {
          return require(`../${urlFile}`);
        } catch (e) {
          return require("../assets/img-placeholder.jpg");
        }
      }
    },

    iconSrc() {
      return (urlIcon) => {
        try {
          return require(`../${urlIcon}`);
        } catch (e) {
          return require("../assets/img-placeholder.jpg");
        }
      }
    }


  },
  setup() {
    const {t} = useI18n();

    const CS_sayhi_research_images = [
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-research.png'), alt: 'Different pictures showing user research activities for the sayhi app redesign'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-wireframe-00.png'), alt: 'Wireframe of the language selection screen'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-wireframe-01.png'), alt: 'Wireframe of conversation screen'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-wireframe-02.png'), alt: 'Wireframe of feedback component'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-user-wireframe-03.png'), alt: 'Wireframe of conversation input'}
    ]
 
    const CS_sayhi_handover_images = [
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-handover-dev-notations-00.png'), alt: 'General overview of the sayhi app documentation handover to development'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-handover-dev-notations-01.png'), alt: 'Design documentation about conversation input'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-handover-dev-notations-02.png'), alt: 'Design documentation about language selection component'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-handover-dev-notations-03.png'), alt: 'Design documentation about language selection component'}
    ] 

    const CS_sayhi_desktop_images = [
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-desktop-dashboard.png'), alt: 'SayHi desktop dashboard overview'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-desktop-translate_02.png'), alt: 'SayHi desktop translation screen'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-desktop-translate.png'), alt: 'SayHi desktop translation screen with translation active'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-desktop-conversation.png'), alt: 'SayHi desktop conversation screen'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-desktop-joining-huddle.png'), alt: 'SayHi desktop huddle screen'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-desktop-huddle.png'), alt: 'SayHi desktop huddle screen'},
      {src: require('../assets/work-gallery/CS-say-hi/CS-sayhi-desktop-settings.png'), alt: 'SayHi desktop huddle screen'}
    ]

    return {t, CS_sayhi_research_images, CS_sayhi_handover_images, CS_sayhi_desktop_images};
  },
};
</script>
<style>

/* Hero section background gradient */
  .hero-sayhi-gradient-bg {
    background-image:
      linear-gradient(
        29.9672deg,
        rgba(255, 255, 255, 0.5) 35.838%,
        rgba(218, 254, 247, 0.5) 75.837%,
        rgba(255, 255, 255, 0.5) 135.98%
      ),
      linear-gradient(
        158.877deg,
        rgba(241, 252, 255, 0.65) 28.608%,
        rgba(133, 141, 255, 0.65) 140.47%
      );
  }

/* Tasks section border gradient */

.task-border-gradient {
  background: rgba(255, 255, 255, 1);
  border-radius: 4px;
  border: 1px solid transparent;
  background-clip: padding-box;
  position: relative;
}

.task-border-gradient::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 4px;
  padding: 1px;
  background:       linear-gradient(
        29.9672deg,
        rgb(19, 200, 255) 35.838%,
        rgb(19, 255, 208) 75.837%,
        rgba(255, 255, 255, 0.5) 135.98%
      );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}
</style>
